import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddAgeCategoryStore} from './parts/AddAgeCategoryStore';
import {DeleteAgeCategoryStore} from './parts/DeleteAgeCategoryStore';
import {EditAgeCategoryStore} from './parts/EditAgeCategoryStore';
import {MoveAgeCategoryStore} from './parts/MoveAgeCategoryStore';
import {ReorderAgeCategoriesStore} from './parts/ReorderAgeCategoriesStore';
import {X1LanguageLoader} from './X1LanguageLoader';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder' | 'Move';

export class X1LanguageStore {
  @observable
  mode: Mode;

  readonly loader: X1LanguageLoader;

  @observable.ref
  addAgeCategory: AddAgeCategoryStore | null;
  @observable.ref
  editAgeCategory: EditAgeCategoryStore | null;
  @observable.ref
  deleteAgeCategory: DeleteAgeCategoryStore | null;
  @observable.ref
  reorder: ReorderAgeCategoriesStore | null;
  @observable.ref
  move: MoveAgeCategoryStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly languageId: number) {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new X1LanguageLoader(languageId);
    this.disposers.add(this.loader);

    this.addAgeCategory = this.editAgeCategory = this.deleteAgeCategory =
      this.move = this.reorder = null;

    this.setup();
  }

  @computed
  get isSaving(): boolean {
    return (this.addAgeCategory?.save?.isPending === true ||
      this.editAgeCategory?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deleteAgeCategory?.save?.isPending === true ||
      this.move?.save?.isPending === true
    );
  }

  @action
  private toViewMode = () => {
    this.mode = 'View';
    this.addAgeCategory = this.editAgeCategory = this.deleteAgeCategory =
      this.move = this.reorder = null;
  };

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addAgeCategory = new AddAgeCategoryStore(this.languageId);
  };

  @action
  completeAdd = () => {
    this.addAgeCategory?.complete();
  };

  @action
  beginEdit = (ageCategoryId: number) => {
    this.mode = 'Edit';
    let ageCategory = this.loader.ageCategoryById[ageCategoryId]!;
    this.editAgeCategory = new EditAgeCategoryStore(ageCategory);
  };

  @action
  completeEdit = () => {
    this.editAgeCategory?.complete();
  };

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  beginMove = (ageCategoryId: number) => {
    this.mode = 'Move';
    const ageCategory = this.loader.ageCategoryById[ageCategoryId];
    this.move = new MoveAgeCategoryStore(ageCategory, this.loader.lookup!);
  };

  @action
  completeMove = () => {
    this.move?.complete();
  };

  @action
  beginDelete = (ageCategoryId: number) => {
    this.mode = 'Delete';
    this.deleteAgeCategory = new DeleteAgeCategoryStore(ageCategoryId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteAgeCategory?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const ageCategoryIds = this.loader.ageCategories.map(x => x.Id);
    this.reorder = new ReorderAgeCategoriesStore(this.languageId, ageCategoryIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };

  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.toViewMode();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addAgeCategory?.save?.state,
        this.editAgeCategory?.save?.state,
        this.deleteAgeCategory?.save?.state,
        this.reorder?.save?.state,
        this.move?.save?.state
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addAgeCategory?.save,
      this.editAgeCategory?.save,
      this.deleteAgeCategory?.save,
      this.reorder?.save,
      this.move?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.refresh();
        this.toViewMode();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
